import { useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TruncatedText } from "@/components/ui/truncated-text";
import { CountBadge } from "@/components/shared/CountBadge";
import { cn } from "@/lib/utils";

export interface QuickFilterOption {
  value: string;
  label: string;
  /** Secondary text, rendered in the overflow list only. */
  meta?: string;
}

interface QuickFilterChipsProps {
  options: QuickFilterOption[];
  value: string;
  /** When non-empty, highlights every option whose value is in the set and `value` is ignored. */
  values?: string[];
  onChange: (value: string) => void;
  label?: string;
  maxVisible?: number;
  overflowLabel?: string;
  searchPlaceholder?: string;
  className?: string;
}

function chipClass(active: boolean): string {
  return cn(
    "h-8 rounded-full border px-3 font-medium text-foreground",
    active
      ? "border-primary bg-primary-tint-medium hover:bg-primary-tint-strong"
      : "border-input bg-card hover:bg-primary/15",
  );
}

export function QuickFilterChips({
  options,
  value,
  values,
  onChange,
  label,
  maxVisible = 4,
  overflowLabel = "More",
  searchPlaceholder = "Search…",
  className,
}: QuickFilterChipsProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const visible = options.slice(0, maxVisible);
  const hidden = options.slice(maxVisible);
  const multi = Boolean(values && values.length > 0);
  const isActive = (optionValue: string): boolean =>
    multi ? (values as string[]).includes(optionValue) : optionValue === value;
  const activeHidden = hidden.filter((option) => isActive(option.value));
  const selectedHidden = activeHidden[0];
  const extraHidden = activeHidden.length - 1;
  const needle = query.trim().toLowerCase();
  const filteredHidden = needle
    ? hidden.filter((option) => option.label.toLowerCase().includes(needle))
    : hidden;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
    }
  };

  const pick = (next: string) => {
    onChange(next);
    handleOpenChange(false);
  };

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      {label && (
        <span className="text-sm font-medium text-muted-foreground">
          {label.endsWith(":") ? label : `${label}:`}
        </span>
      )}

      {visible.map((option) => (
        <Button
          key={option.value}
          variant="secondary"
          size="sm"
          aria-pressed={isActive(option.value)}
          onClick={() => onChange(option.value)}
          className={chipClass(isActive(option.value))}
        >
          {option.label}
        </Button>
      ))}

      {hidden.length > 0 && (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              aria-pressed={Boolean(selectedHidden)}
              className={cn(chipClass(Boolean(selectedHidden)), open && "ring-2 ring-primary/20")}
            >
              {selectedHidden ? (
                extraHidden > 0 ? `${selectedHidden.label} +${extraHidden}` : selectedHidden.label
              ) : (
                <>
                  {overflowLabel}
                  <CountBadge count={hidden.length} />
                </>
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-4">
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 pl-9"
              />
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {filteredHidden.length === 0 ? (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">No matches</p>
              ) : (
                filteredHidden.map((option) => {
                  const active = isActive(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => pick(option.value)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                        active
                          ? "bg-primary-tint-soft text-foreground"
                          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                      )}
                    >
                      <TruncatedText className="min-w-0 flex-1">{option.label}</TruncatedText>
                      {option.meta && (
                        <span className="text-xs text-muted-foreground">{option.meta}</span>
                      )}
                      {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
